import axios from 'axios'
import { ElMessage } from 'element-plus'
import { i18nConfig } from '@/utils/ruoyi'

// 记录强制刷新次数的 key,用于防止无限刷新循环
const RELOAD_TIMES_KEY = 'kl-force-reload-times'
// 强制刷新时在地址栏上携带的参数名(值为目标版本号)
export const VERSION_PARAM = '_v'

// 防止同一次失败中重复触发刷新
let reloading = false

/**
 * 获取服务端最新的前端版本号
 * @returns {Promise<string|null>} 版本号,获取失败返回 null
 */
export const fetchLatestVersion = async () => {
  try {
    // 追加时间戳,防止 version.json 被浏览器或代理缓存
    const res = await axios.get(`${import.meta.env.BASE_URL}/version.json?t=${Date.now()}`)
    return res?.data?.version
  } catch (e) {
    return null
  }
}

/**
 * 强制刷新页面,彻底绕过浏览器缓存
 *
 * 背景:发版后 dist 目录被整体覆盖,带 hash 的旧 js 文件已被删除。
 * 若浏览器缓存了旧的 index.html,旧页面在加载动态路由组件时会请求到已不存在的 js(404),
 * 导致点击菜单无法跳转。而 location.reload() 属于软刷新,文档与子资源依然走缓存,解决不了这个问题。
 *
 * 方案:给文档地址追加 _v 参数,地址变化后 index.html 必然发起新的网络请求,
 * 新 index.html 里引用的 hash 资源地址同样是全新的,相当于整包换新。
 *
 * @param {string} [tip] 刷新前的提示文案
 */
export const forceReload = async (tip) => {
  if (reloading) return
  reloading = true
  // 防止无限刷新:同一标签页累计刷新超过 2 次后停下来,交给用户手动强刷
  const times = Number(sessionStorage.getItem(RELOAD_TIMES_KEY) || 0) + 1
  sessionStorage.setItem(RELOAD_TIMES_KEY, times)
  if (times > 2) {
    reloading = false
    ElMessage.error(i18nConfig('页面资源加载失败,请按 Ctrl+F5 强制刷新浏览器'))
    return
  }
  if (tip) {
    ElMessage.warning(i18nConfig(tip))
  }
  // 把最新版本号一起带上,新页面加载后可据此确认自己已是最新版本,从而避免反复刷新
  const version = await fetchLatestVersion()
  const url = new URL(window.location.href)
  url.searchParams.set(VERSION_PARAM, version || Date.now())
  window.location.replace(url.toString())
}

/**
 * 清理地址栏上残留的 _v 参数
 *
 * 注意:必须在 router.isReady() 之后调用,并且要保留 history.state,
 * 否则会破坏 vue-router 维护的内部状态。
 */
export const clearForceReloadParam = () => {
  const url = new URL(window.location.href)
  if (!url.searchParams.has(VERSION_PARAM)) return
  url.searchParams.delete(VERSION_PARAM)
  window.history.replaceState(window.history.state, '', url.toString())
}

/**
 * 清空强制刷新计数(确认当前已是最新版本时调用)
 */
export const resetForceReloadTimes = () => {
  sessionStorage.removeItem(RELOAD_TIMES_KEY)
}